import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// CUSTOM
import ClickableMobileStepper from '@/ui/PublicPage/ClickableMobileStepper';

// Types
import { ParsedRouteWarning } from '@/@types/services/mapService';

type PaneWarningsProps = {
  warnings: ParsedRouteWarning[];
};

const PaneWarnings: FC<PaneWarningsProps> = ({ warnings }) => {
  const { t } = useTranslation();

  const [activeSteps, setActiveSteps] = useState<Record<string, number>>({});
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const warningGroups = useMemo(() => {
    return [
      {
        key: 'MSP',
        title: t('translation:titles.warning_msp_singular'),
        warnings: warnings.filter((warning) => warning.warning_type === 'MSP'),
      },
      {
        key: 'podrucja',
        title: t('translation:titles.warning_podrucje_singular'),
        warnings: warnings.filter((warning) => warning.warning_type === 'podrucja'),
      },
    ].filter((group) => group.warnings.length > 0);
  }, [warnings, t]);

  useEffect(() => {
    setActiveSteps((prevState) => {
      return warningGroups.reduce((acc, group) => {
        const previousStep = prevState[group.key] ?? 0;
        acc[group.key] = Math.min(previousStep, group.warnings.length - 1);
        return acc;
      }, {} as Record<string, number>);
    });
  }, [warningGroups]);

  if (warningGroups.length === 0) {
    return null;
  }

  return (
    <Stack mx={2}>
      {warningGroups.map((group, index) => (
        <React.Fragment key={group.key}>
          <Box
            my={1}
            onTouchStart={(e: React.TouchEvent) => {
              setTouchEnd(null);
              setTouchStart(e.targetTouches[0].clientX);
            }}
            onTouchMove={(e: React.TouchEvent) => {
              setTouchEnd(e.targetTouches[0].clientX);
            }}
            onTouchEnd={() => {
              if (touchStart === null || touchEnd === null || group.warnings.length <= 1) return;

              const distance = touchStart - touchEnd;
              const isLeftSwipe = distance > minSwipeDistance;
              const isRightSwipe = distance < -minSwipeDistance;

              if (isRightSwipe) {
                setActiveSteps((prevState) => ({
                  ...prevState,
                  [group.key]: Math.max(0, (prevState[group.key] ?? 0) - 1),
                }));
              } else if (isLeftSwipe) {
                setActiveSteps((prevState) => ({
                  ...prevState,
                  [group.key]: Math.min(group.warnings.length - 1, (prevState[group.key] ?? 0) + 1),
                }));
              }
            }}
          >
            <Typography variant="h6" color={group.key === 'MSP' ? 'error' : 'primary'}>
              {group.title}
            </Typography>

            <Box>
              <Typography variant="body2" color={group.key === 'MSP' ? 'error' : 'primary'}>
                {t(`translation:${group.warnings[activeSteps[group.key] ?? 0].message_ttoken}`)}
              </Typography>
            </Box>

            {group.warnings.length > 1 ? (
              <Box display="flex" justifyContent="center" alignItems="flex-end" mt={0.5}>
                <ClickableMobileStepper
                  activeStep={activeSteps[group.key] ?? 0}
                  onStepClick={(step: number) => {
                    setActiveSteps((prevState) => ({
                      ...prevState,
                      [group.key]: step,
                    }));
                  }}
                  steps={group.warnings.length}
                  activeColor={group.key === 'MSP' ? 'error' : 'primary'}
                />
              </Box>
            ) : null}
          </Box>
          <Divider />
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default PaneWarnings;
